import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button } from '@3plearning/react-components';
import { t } from '@lingui/macro';
import moment from 'moment';
import { useAnalytics } from 'use-analytics';
import { AssignIcon, IconAssignLater } from '../../helpers/icon-dictionary';
import ActionsList from '../actions-list';
import OutsideClickHandler from '../../helpers/hoc/close/outside-click-handler';
import Resources from '../../helpers/resources';
import { mapSelectedActivitiesToReq } from '../../helpers/resources-helper';
import { ERROR_DETECTED } from '../../containers/course-assign/course-assign-types';
import { getTopics } from '../../containers/course-assign/course-assign-actions';
import { showToast } from '../toast/toast-action';
import { getHyphenDateFormat, getDueDateISOString } from '../../helpers/utils';
import {
  useAssignmentFlowTracking,
  useTracking,
  summarizeAssignments
} from '../../analytics';

function AssignButton(props) {
  const { i18n } = props;
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const assignFlow = useAssignmentFlowTracking('assign');
  const { trackMenuAction } = useTracking();
  const [isAssignActionsOpen, setAssignActionsOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const assignButtonText = `${i18n._(t('footer.assign')`Assign`)} (${
    props.assignmentsToAssign.length
  })`;

  function displayPreference() {
    return `${props.startDatePreference[0]} - ${
      props.dueDatePreference !== undefined
        ? props.dueDatePreference[0]
        : i18n._(t('footer.assignNoDueDate')`Assign with no due date`)
    }`;
  }

  function handleSuccess() {
    const { classroomId, groupId } = props;
    dispatch(getTopics(classroomId, groupId));
    setAssignActionsOpen(false);
    setIsAssigning(false);
    track('ActivityAssigned');
    assignFlow.confirm({
      target: 'students',
      ...summarizeAssignments(props.assignmentsToAssign)
    });
    dispatch(
      showToast(
        i18n._(
          t(
            'footer.assignSuccess'
          )`Selected student activities have been assigned`
        ),
        'success'
      )
    );
  }

  function handleAssign() {
    if (isAssigning) {
      return;
    }
    const {
      classroomId,
      groupId,
      courseId,
      startDatePreference,
      dueDatePreference,
      assignmentsToAssign
    } = props;
    const dueDate = getDueDateISOString(dueDatePreference);
    const requestBody = {
      courseId,
      startDate:
        startDatePreference &&
        moment(
          `${getHyphenDateFormat(startDatePreference[0])} ${getHyphenDateFormat(
            startDatePreference[1]
          )}`
        ).toISOString(),
      dueDate,
      hasDueDate: dueDatePreference !== undefined,
      ...mapSelectedActivitiesToReq(assignmentsToAssign)
    };
    trackMenuAction({ action: 'assign-now' });
    setIsAssigning(true);
    setAssignActionsOpen(false);
    Resources.assign
      .post({ classroomId, groupId })
      .withBody(requestBody)
      .send()
      .then(
        () => handleSuccess(),
        (err) => {
          setIsAssigning(false);
          dispatch({
            type: ERROR_DETECTED,
            payload: err
          });
        }
      );
  }

  const assignActionsListOptions = [
    {
      icon: IconAssignLater,
      disabled: isAssigning,
      text: i18n._(t('footer.assignLater')`Assign Later`),
      subtext: i18n._(t('footer.assignLaterSubtext')`Modify assign dates`),
      onClick: () => {
        trackMenuAction({ action: 'assign-later' });
        dispatch({ type: 'SHOW_ASSIGN_LATER' });
      }
    },
    {
      icon: AssignIcon,
      text: i18n._(t('footer.assignNow')`Assign Now`),
      disabled: isAssigning,
      subtext:
        props.startDatePreference !== undefined
          ? displayPreference()
          : i18n._(t('footer.assignNoDueDate')`Assign with no due date`),
      onClick: handleAssign
    }
  ];

  return (
    <>
      <div className="assign">
        <OutsideClickHandler
          callback={() => setAssignActionsOpen(false)}
          forButton="assignActions"
        >
          <div
            className="actionsContainer"
            id="assignActions"
            data-testid="assign-button"
          >
            <Button
              className="button"
              disabled={props.assignmentsToAssign.length === 0}
              primary
              data-testid="assign-button"
              text={assignButtonText}
              type={Button.LOZENGE}
              onClick={() => setAssignActionsOpen(!isAssignActionsOpen)}
              alignIcon="LEFT"
              icon={AssignIcon}
            />

            <ActionsList
              options={assignActionsListOptions}
              isOpen={isAssignActionsOpen}
              isAssigning
              isWide
              shouldOpenUp
            />
          </div>
        </OutsideClickHandler>
      </div>
    </>
  );
}

AssignButton.propTypes = {
  i18n: PropTypes.object,
  classroomId: PropTypes.number,
  courseId: PropTypes.number,
  groupId: PropTypes.number,
  startDatePreference: PropTypes.arrayOf(PropTypes.string),
  dueDatePreference: PropTypes.arrayOf(PropTypes.string),
  assignmentsToAssign: PropTypes.arrayOf(PropTypes.object)
};

export default AssignButton;



// WEBPACK FOOTER //
// ./components/assign-button/index.js